/**
 * Spec → IR. Deterministic, cached, no model in the loop.
 *
 * Two invariants this file exists to hold:
 *
 *  1. **Same spec in, same IR out, byte for byte.** Not "equivalent" — identical.
 *     Determinism is what lets the IR be cached to disk and restored on warm
 *     open without re-deriving anything, which is most of why Bear feels
 *     instant.
 *  2. **The design compiler is never on this path.** Compilation is a build
 *     step whose output is cached, not a render step. A compiler that runs per
 *     keystroke is disqualifying — the budget is 8.3ms at 120Hz.
 */

import {
  ROLE_KIND, DOCK_EDGES, ATTACHMENTS, EXTENTS, SOURCES, REGION_PURPOSES, APP_TEMPLATES,
} from '@morph/spec';
import type { TLayoutSpec, TNode, Role } from '@morph/spec';
import type { IR, IRNode, IRPlacement, ViewportClass } from './ir.js';
import { resolveTokens } from './tokens.js';
import { checkFloor, type Violation } from './a11y.js';

export class RefusedLayout extends Error {
  constructor(readonly violations: readonly Violation[]) {
    super(
      `runtime refused the layout: ${violations.length} floor violation(s)\n  - ` +
        violations.map((v) => `${v.code}: ${v.message}`).join('\n  - '),
    );
    this.name = 'RefusedLayout';
  }
}

/** Recursively order object keys before hashing. A JSON.stringify replacer made
 * from only the spec's top-level keys would act as a whitelist at every depth,
 * collapsing most distinct layout trees to the same cache key. */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, child]) => [key, canonicalize(child)]),
    );
  }
  return value;
}

/** FNV-1a over canonical JSON. Cheap, stable, and enough for a cache key —
 *  this is not a security boundary. */
function hash(value: unknown): string {
  const json = JSON.stringify(canonicalize(value));
  let h = 2166136261;
  for (let i = 0; i < json.length; i += 1) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

/** Above this, a collection is virtualized rather than materialized. Nothing
 *  in the spec says how many objects there are, so the caller supplies it. */
export const VIRTUALIZE_ABOVE = 60;

/**
 * Membership, not a cast.
 *
 * `as never` told the compiler these were fine and told the reader nothing; an
 * adversarial pass then walked an arbitrary string through into the markup.
 * `compileSpec` runs on specs the validator did not see, so it cannot assume
 * any scalar came from its closed set.
 *
 * Module scope because it now guards two vocabularies — placement and `source`
 * — and a second copy is how the two drift apart.
 */
const inSet = <T extends string>(v: unknown, set: readonly T[]): T | undefined =>
  typeof v === 'string' && (set as readonly string[]).includes(v) ? v as T : undefined;

export interface CompileContext {
  viewport: ViewportClass;
  /** Observed item count, for the virtualization decision. */
  itemCount?: number;
}

/**
 * Which player a role needs.
 *
 * Derived from the ROLE, not from the value — the value is a blob reference the
 * renderer never opens, and sniffing it would put content inspection in the
 * presentation layer. `media.video` is a video because the vocabulary says so.
 */
function mediumFor(role: Role): 'still' | 'video' | 'audio' | 'document' {
  if (role === 'media.video') return 'video';
  if (role === 'media.audio') return 'audio';
  if (role === 'media.document') return 'document';
  return 'still';
}

export function compileSpec(spec: TLayoutSpec, ctx: CompileContext): IR {
  const nodes: IRNode[] = [];
  const order: number[] = [];

  // An anchor names a node that may compile AFTER the node referring to it, so
  // it cannot be resolved during the walk. Collected and patched once every
  // index exists.
  const idToIndex = new Map<string, number>();
  const pendingAnchors: { at: number; id: string; rest: Omit<
    Extract<IRPlacement, { mode: 'anchored' }>, 'mode' | 'anchor'> }[] = [];
  /** Which capability slot each child came from, applied after the walk for the
   *  same reason anchors are: the node object is frozen into `nodes` on the way
   *  down. */
  const slotOf: { at: number; slot: string }[] = [];

  const emit = (node: TNode, parent: number, depth: number): number => {
    const i = nodes.length;
    const children: number[] = [];
    // Reserve the slot before descending, so indices follow pre-order and a
    // renderer can iterate `order` without ever walking the tree.
    type Mutable = { -readonly [K in keyof IRNode]: IRNode[K] };
    const partial: Mutable = { i, parent, depth, children, kind: node.kind };
    nodes.push(partial as IRNode);
    order.push(i);

    const invocation = (node as { invocation?: IRNode['invocation'] }).invocation;
    if (invocation) partial.invocation = invocation;

    // REGIONS only, and only the values the vocabulary defines. Membership
    // rather than a cast, the same rule `placement` follows two blocks down:
    // an arbitrary string arriving here would reach the markup as a class.
    if (node.kind === 'region') {
      const purpose = inSet((node as { purpose?: string }).purpose, REGION_PURPOSES);
      if (purpose !== undefined) partial.purpose = purpose;
      // A PURPOSED region carries its authored id, as a collection does: a
      // sidebar's tabs name it, so the host can hold Obsidian's left and
      // right sidebars to different pane choices (`workspace.sidePanes`).
      // Measured missing: every tab rendered without `data-ws-sidebar` and
      // every choice fell through to the first pane.
      const rid = (node as { id?: string }).id;
      if (purpose !== undefined && typeof rid === 'string' && rid) partial.specId = rid;
    }

    const nodeId = (node as { id?: string }).id;
    if (nodeId !== undefined) idToIndex.set(nodeId, i);

    // The root IS the surface, so a placement on it has nothing to sit within.
    // The validator refuses it; this is the fail-closed half, because compile
    // also runs on specs that came from the generator rather than the validator.
    const place = parent === -1
      ? undefined
      : (node as { placement?: {
          mode: string; edge?: string; rest?: string; anchor?: string;
          attachment?: string; extent?: string; collapsible?: boolean } }).placement;
    // Only a node that can BE a panel. The schema grants `placement` to region,
    // collection, card and capability; reading it off any node meant a bare
    // floating `field` compiled and rendered, with no container and no
    // accessible name.
    const placeable = node.kind === 'region' || node.kind === 'collection'
      || node.kind === 'card' || node.kind === 'capability';

    if (place && place.mode !== 'inline' && placeable) {
      // Membership, not a cast. `as never` told the compiler these were fine
      // and told the reader nothing; an adversarial pass then walked an
      // arbitrary string through here and into the markup. This function's own
      // comment says it runs on specs the validator did not see, so it cannot
      // assume any of these came from their closed set.
      const extent = inSet(place.extent, EXTENTS);
      const shared = {
        ...(extent !== undefined ? { extent } : {}),
        ...(typeof place.collapsible === 'boolean' ? { collapsible: place.collapsible } : {}),
      };
      const edge = inSet(place.edge, DOCK_EDGES);
      const rest = inSet(place.rest, ATTACHMENTS);
      const attachment = inSet(place.attachment, ATTACHMENTS);

      if (place.mode === 'docked' && edge) {
        partial.placement = { mode: 'docked', edge, ...shared };
      } else if (place.mode === 'floating' && rest) {
        partial.placement = { mode: 'floating', rest, ...shared };
      } else if (place.mode === 'anchored' && attachment && typeof place.anchor === 'string') {
        pendingAnchors.push({ at: i, id: place.anchor, rest: { attachment, ...shared } });
      }
      // Anything else falls through to inline, which is the safe mode — the
      // same choice an unresolvable anchor makes below.
    }

    switch (node.kind) {
      case 'region':
        partial.kind = 'region';
        partial.flow = node.flow;
        if (node.labelRole) partial.nameFrom = node.labelRole;
        for (const c of node.children) children.push(emit(c, i, depth + 1));
        break;

      case 'collection': {
        partial.kind = 'collection';
        partial.flow = node.flow;
        // Membership, not a copy. An unchecked value here is worse than
        // useless: `source: "constructor"` resolves a FUNCTION off the prototype
        // of an ordinary `sources` object literal, and the renderer then throws
        // on `.slice()`. Absent means `subject`, and so does anything the
        // vocabulary does not name. Found by @Codex.
        {
          const source = inSet(node.source, SOURCES);
          if (source !== undefined && source !== 'subject') partial.source = source;
        }
        // The authored id, so a host can hand THIS collection its own rows.
        // The field already existed for node-slots, which need it to trace a
        // click to its grant; a collection needs the same identifier for a
        // different reason. See `IRNode.specId`.
        if (typeof node.id === 'string' && node.id) partial.specId = node.id;
        if (node.affordances) partial.affordances = node.affordances;
        partial.virtualize = (ctx.itemCount ?? 0) > VIRTUALIZE_ABOVE;
        // Carry the query. Dropping it here meant `groupBy` never reached a
        // renderer, so a grouped collection rendered as a flat one and the
        // generator's decision vanished with no error anywhere.
        if (node.query) {
          const q: {
            groupBy?: typeof node.query.groupBy;
            subGroupBy?: typeof node.query.subGroupBy;
            sort?: typeof node.query.sort;
            aggregate?: typeof node.query.aggregate;
          } = {};
          if (node.query.groupBy) q.groupBy = node.query.groupBy;
          // The third field to be dropped here and the third to be found the
          // same way. `subGroupBy` only means anything inside a `groupBy`, and
          // `validate.ts` already refuses one without the other — so carrying
          // it unconditionally would let a malformed spec through this layer on
          // the strength of a check made in another. Carried WITH its partner.
          if (node.query.groupBy && node.query.subGroupBy) q.subGroupBy = node.query.subGroupBy;
          if (node.query.sort) q.sort = node.query.sort;
          // The other half of why `aggregate` had four files maintaining it and
          // no effect: even a host that computed the numbers had no way to know
          // which folds the spec wanted drawn, because the IR did not carry
          // them. Exactly the bug the comment above describes for `groupBy`.
          if (node.query.aggregate?.length) q.aggregate = node.query.aggregate;
          if (q.groupBy || q.sort || q.aggregate) partial.query = q;
        }
        const templates: { when?: { has: Role }; root: number }[] = [];
        for (const item of node.items) {
          const root = emit(item.spec, i, depth + 1);
          children.push(root);
          templates.push(item.when !== undefined ? { when: item.when, root } : { root });
        }
        partial.itemTemplates = templates;
        break;
      }

      case 'card':
        partial.kind = 'card';
        if (node.affordances) partial.affordances = node.affordances;
        // A card's accessible name comes from its first text-bearing binding.
        // Deciding this here rather than in the renderer means every renderer
        // agrees, which is the point of having an IR at all.
        for (const b of node.bindings) {
          const bi = nodes.length;
          // A binding's IR kind follows its ROLE KIND, not its position. Emitting
          // every binding as a field made a media binding render as the literal
          // blob reference — and made a poster-only card look nameable to the
          // accessibility floor. One cause, two visible bugs.
          const kind = ROLE_KIND[b.role];
          // `doc` joins the two for the same reason `av` did: a document's
          // value is a blob the renderer resolves, so it must reach the media
          // path rather than the field path — a field would print the blob
          // reference as text, which is the bug the comment above records.
          const isMedia = kind === 'media' || kind === 'av' || kind === 'doc';
          nodes.push({
            i: bi, kind: isMedia ? 'media' : 'field',
            parent: i, depth: depth + 1, children: [],
            role: b.role, direction: b.direction,
            // A derived column carries its word into the IR. Dropped here, the
            // renderer would print the stored value under a header that says
            // "Words" — accepted by the schema and rendered as something else,
            // which is the shape the slot names and the aspect ratio both had.
            ...(b.derive ? { derive: b.derive } : {}),
            // The same rule one field over. A lookup dropped here renders as
            // this object's own value for `role`, under a heading naming a role
            // it never read — the exact substitution the line above exists to
            // stop, which is why it is written beside it rather than somewhere
            // a later reader has to find.
            ...(b.lookup ? { lookup: b.lookup } : {}),
            // A still gets an aspect; a recording does not have one, and giving
            // audio a 2/3 box would draw a tall empty rectangle with a
            // transport at the bottom.
            ...(isMedia
              ? { fit: 'cover' as const, medium: mediumFor(b.role),
                  ...(kind === 'media' ? { aspect: '2/3' as const } : {}) }
              : {}),
            ...(b.mutation ? { mutation: b.mutation } : {}),
            // A binding you can type into is an interactive node, and the IR
            // used to say otherwise by omission: affordances were copied from
            // region, collection and card specs only, so every check written
            // over `n.affordances` — the whole accessibility floor — looked past
            // the editable set entirely.
            ...(b.direction === 'read-write' ? { affordances: ['edit-inline' as const] } : {}),
          } as IRNode);
          order.push(bi);
          children.push(bi);
          if (!partial.nameFrom && (b.role === 'title' || b.role === 'subtitle')) {
            partial.nameFrom = b.role;
          }
        }
        for (const c of node.children ?? []) children.push(emit(c, i, depth + 1));
        break;

      case 'field':
        partial.kind = 'field';
        partial.role = node.binding.role;
        partial.direction = node.binding.direction;
        if (node.binding.mutation) partial.mutation = node.binding.mutation;
        if (node.binding.direction === 'read-write') partial.affordances = ['edit-inline'];
        break;

      case 'media':
        partial.kind = 'media';
        partial.role = node.binding.role;
        partial.direction = node.binding.direction;
        partial.fit = node.fit;
        partial.medium = mediumFor(node.binding.role);
        if (node.optional) partial.optional = true;
        if (node.aspect) partial.aspect = node.aspect;
        break;

      case 'node-slot':
        partial.kind = 'node-slot';
        partial.role = node.via;
        // maxDepth is not a performance knob. An object graph with a cycle
        // renders forever without it, so it is a safety bound and it has to
        // reach the renderer to be one.
        partial.maxDepth = node.maxDepth;
        if (node.mutations?.length) {
          partial.mutations = [...node.mutations];
          // Only when it is load-bearing. An id carried on every node would be
          // a second address for a thing the index already names, and the two
          // would have to agree forever.
          partial.specId = node.id;
        }
        // The spec a referenced object is drawn with. Emitted like any other
        // children slot; the renderer draws them against the TARGET rather than
        // the subject, which is the whole of what "a region hosts a spec" means
        // at this layer.
        for (const c of node.children ?? []) children.push(emit(c, i, depth + 1));
        break;

      case 'capability':
        partial.kind = 'capability';
        partial.capability = node.capability;
        partial.capabilityVersion = node.version;
        if (node.config) partial.config = node.config;
        if (node.channels) partial.channels = Object.fromEntries(
          Object.entries(node.channels).map(([name, binding]) => [name, {
            role: binding.role,
            direction: binding.direction,
            ...(binding.mutation ? { mutation: binding.mutation } : {}),
          }]),
        );
        // The slot NAME travels with the child. It used to be dropped here —
        // `Object.values` keeps the arrays and discards the keys — which made a
        // header and a footer indistinguishable by the time anything could act
        // on them.
        for (const [slot, kids] of Object.entries(node.slots ?? {})) {
          for (const c of kids) {
            const ci = emit(c, i, depth + 1);
            children.push(ci);
            slotOf.push({ at: ci, slot });
          }
        }
        break;
    }
    return i;
  };

  const root = emit(spec.root, -1, 0);

  // Anchors, now that every index exists.
  //
  // An anchor that resolves to nothing falls back to INLINE rather than to a
  // floating panel pinned to the surface corner. The validator refuses a
  // dangling anchor upstream, so reaching here means the spec skipped it — and
  // of the two failure modes, a panel that stays in the flow is reachable and
  // legible, while one absolutely positioned against nothing sits on top of the
  // content it was meant to sit beside. Fail closed means fail to the safe
  // mode, not to the dramatic one.
  for (const s of slotOf) {
    (nodes[s.at] as { slot?: string }).slot = s.slot;
  }

  for (const p of pendingAnchors) {
    const target = idToIndex.get(p.id);
    if (target === undefined || target === p.at) continue;
    (nodes[p.at] as { placement?: IRPlacement }).placement =
      { mode: 'anchored', anchor: target, ...p.rest };
  }

  const tokens = resolveTokens(spec.density, spec.typography, spec.palette, ctx.viewport);

  const ir: IR = {
    specVersion: spec.specVersion,
    vocabularyVersion: spec.vocabularyVersion,
    hash: hash(spec),
    tokens, nodes, root, order,
    viewportClass: ctx.viewport,
    // The named app look — membership, not a cast, for the reason every
    // other closed word here takes: this runs on specs the validator did not
    // see, and the id becomes a class on the root.
    ...(() => {
      const t = (spec as { template?: { id?: unknown; version?: unknown } }).template;
      const id = inSet(t?.id, APP_TEMPLATES);
      return id !== undefined && typeof t?.version === 'string'
        ? { template: { id, version: t.version } } : {};
    })(),
    // WHAT THE LAYOUT IS ABOUT (task 0176). Membership-checked for the same
    // reason the template id is: this runs on specs the validator did not see.
    ...(() => {
      const c = (spec as { target?: { cardinality?: unknown } }).target?.cardinality;
      return c === 'single' || c === 'small-set' || c === 'large-set' ? { cardinality: c } : {};
    })(),
    // The declared view-state slots. Dropped here until now, which is the
    // fourth field this function has been found silently discarding —
    // `groupBy`, `aggregate` and `subGroupBy` were the others, and the comment
    // beside the query says so about each of them. A spec could declare a
    // filter reading slot 2 and nothing between the schema and the store ever
    // knew slot 2 existed.
    ...(spec.viewState?.length
      ? { viewState: spec.viewState as NonNullable<IR['viewState']> } : {}),
  };

  const violations = checkFloor(ir, ctx.viewport);
  if (violations.length) throw new RefusedLayout(violations);
  return ir;
}

/**
 * Compiled-spec cache.
 *
 * `architecture.md` says view state does not live in the object store and never
 * said where it *does* live. This is half of that answer: compiled layouts are
 * a pure function of (spec, viewport), so they are cacheable by content hash
 * and restorable without re-deriving. The other half — scroll offset, caret,
 * collapse — is per-device and belongs beside this, not in it.
 */
export class SpecCache {
  private readonly map = new Map<string, IR>();
  constructor(private readonly limit = 64) {}

  private key(spec: TLayoutSpec, ctx: CompileContext): string {
    return `${hash(spec)}:${ctx.viewport}:${(ctx.itemCount ?? 0) > VIRTUALIZE_ABOVE}`;
  }

  get(spec: TLayoutSpec, ctx: CompileContext): IR {
    const k = this.key(spec, ctx);
    const hit = this.map.get(k);
    if (hit) {
      this.map.delete(k);       // LRU: refresh recency
      this.map.set(k, hit);
      return hit;
    }
    const ir = compileSpec(spec, ctx);
    this.map.set(k, ir);
    if (this.map.size > this.limit) this.map.delete(this.map.keys().next().value!);
    return ir;
  }

  get size(): number { return this.map.size; }
}

